import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AMCTestSelection from './AMCTestSelection';
import AMCTestInterface from './AMCTestInterface';
import AMCTestResults from './AMCTestResults';
import { useUser } from '../contexts/UserContext';

type AMCView = 'selection' | 'test' | 'results';

interface AMCState {
  view: AMCView;
  selectedContestId: string | null;
}

const AMCDashboard: React.FC = () => {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();

  const [state, setState] = useState<AMCState>({
    view: 'selection',
    selectedContestId: null
  });

  useEffect(() => {
    if (!isLoading && !user) navigate('/login');
  }, [user, isLoading, navigate]);

  if (isLoading || !user) return null;

  const handleTestSelect = (contestId: string, isCompleted?: boolean) => {
    // If test is completed, go directly to results view
    if (isCompleted) {
      setState({
        view: 'results',
        selectedContestId: contestId
      });
    } else {
      setState({
        view: 'test',
        selectedContestId: contestId
      });
    }
  };

  const handleTestComplete = () => {
    setState(prev => ({
      ...prev,
      view: 'results'
    }));
  };

  const handleBackToSelection = () => {
    setState({
      view: 'selection',
      selectedContestId: null
    });
  };

  const handleRetakeTest = () => {
    setState(prev => ({
      ...prev,
      view: 'test'
    }));
  };

  const handleBackToSelectionFromResults = () => {
    setState({
      view: 'selection',
      selectedContestId: null
    });
  };

  const headerLinkStyle: React.CSSProperties = {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#334155',
    cursor: 'pointer'
  };

  const renderSelection = () => (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '16px 24px 0'
        }}
      >
        <button style={headerLinkStyle} onClick={() => navigate('/amc-analytics')}>
          📊 Analytics
        </button>
        <button style={headerLinkStyle} onClick={() => navigate('/amc-missed-questions')}>
          ❌ Missed Questions
        </button>
      </div>
      <AMCTestSelection onTestSelect={handleTestSelect} />
    </div>
  );

  switch (state.view) {
    case 'selection':
      return renderSelection();

    case 'test':
      if (!state.selectedContestId) {
        setState({ view: 'selection', selectedContestId: null });
        return null;
      }
      return (
        <AMCTestInterface
          contestId={state.selectedContestId}
          onComplete={handleTestComplete}
          onBack={handleBackToSelection}
        />
      );

    case 'results':
      if (!state.selectedContestId) {
        setState({ view: 'selection', selectedContestId: null });
        return null;
      }
      return (
        <AMCTestResults
          contestId={state.selectedContestId}
          onBack={handleBackToSelectionFromResults}
          onRetakeTest={handleRetakeTest}
        />
      );

    default:
      return renderSelection();
  }
};

export default AMCDashboard;
